import { Box } from "@mui/material";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { SALESFORCE_TOOLTIP_COLORS } from "../lib/salesforceDashboard";

// Salesforce places the tooltip 25px right of and 18px above the pointer and flips it left at the window edge.
const POINTER_OFFSET_X_PX = 25;
const POINTER_OFFSET_Y_PX = 18;
const MARK_BAR_WIDTH_PX = 4;
const MARK_SELECTOR = ".recharts-bar-rectangle, .recharts-pie-sector, .recharts-pie-label-text, .recharts-dot";

export type TooltipRow = { label: string; value: string; color?: string };

export type TooltipContent = {
  // Dimension lines above the separator; a row with a colour gets the marking bar.
  rows: TooltipRow[];
  metric: TooltipRow;
  share?: string;
};

type Hover = { x: number; y: number; content: TooltipContent };

export const useChartHover = () => {
  const [hover, setHover] = useState<Hover | null>(null);
  const show = useCallback(
    (event: MouseEvent, content: TooltipContent) => setHover({ x: event.clientX, y: event.clientY, content }),
    []
  );
  const hide = useCallback(() => setHover(null), []);
  // Recharts does not reliably report the pointer leaving a mark, and a fast exit skips the chart entirely,
  // so while a tooltip is open the document itself watches for the pointer leaving the marks.
  const isOpen = hover !== null;
  useEffect(() => {
    if (!isOpen) return undefined;
    const hideOutsideMarks = (event: globalThis.MouseEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest(MARK_SELECTOR)) setHover(null);
    };
    const hideOnLeavingDocument = () => setHover(null);
    document.addEventListener("mousemove", hideOutsideMarks);
    document.documentElement.addEventListener("mouseleave", hideOnLeavingDocument);
    return () => {
      document.removeEventListener("mousemove", hideOutsideMarks);
      document.documentElement.removeEventListener("mouseleave", hideOnLeavingDocument);
    };
  }, [isOpen]);
  return { hover, show, hide };
};

const colors = SALESFORCE_TOOLTIP_COLORS;

const Line = ({ row }: { row: TooltipRow }) => (
  <Box sx={{ display: "flex", alignItems: "stretch", gap: "8px" }}>
    {row.color && (
      <Box aria-hidden sx={{ flex: "none", width: `${MARK_BAR_WIDTH_PX}px`, backgroundColor: row.color }} />
    )}
    <Box sx={{ display: "flex", flex: 1, justifyContent: "space-between", gap: "16px" }}>
      <Box sx={{ color: colors.label }}>{row.label}</Box>
      <Box sx={{ textAlign: "right" }}>{row.value}</Box>
    </Box>
  </Box>
);

export const SalesforceHoverTooltip = ({ hover }: { hover: Hover | null }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!hover || !ref.current) {
      setPosition(null);
      return;
    }
    const { width, height } = ref.current.getBoundingClientRect();
    const overflowsRight = hover.x + POINTER_OFFSET_X_PX + width > window.innerWidth;
    const left = overflowsRight ? hover.x - POINTER_OFFSET_X_PX - width : hover.x + POINTER_OFFSET_X_PX;
    const top = Math.min(Math.max(hover.y - POINTER_OFFSET_Y_PX, 0), window.innerHeight - height);
    setPosition({ left: Math.max(left, 0), top });
  }, [hover]);

  if (!hover) return null;
  const { rows, metric, share } = hover.content;

  return createPortal(
    <Box
      ref={ref}
      role="tooltip"
      sx={{
        position: "fixed",
        zIndex: theme => theme.zIndex.tooltip,
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? "visible" : "hidden",
        p: "12px",
        backgroundColor: colors.background,
        borderRadius: "3px",
        color: colors.value,
        fontSize: "13px",
        lineHeight: "20px",
        pointerEvents: "none",
        whiteSpace: "pre-line",
      }}
    >
      {rows.map(row => (
        <Line key={`${row.label}-${row.value}`} row={row} />
      ))}
      <Box sx={{ my: "4px", borderTop: `1px solid ${colors.label}` }} />
      <Line row={metric} />
      {share && <Box sx={{ color: colors.label, fontSize: "10.4px", textAlign: "right" }}>{share}</Box>}
    </Box>,
    document.body
  );
};
